import React, { useState, useEffect, useRef } from 'react';
import {
  DialogueBox,
  DropdownContainer,
  DropdownItem,
  DropdownBox,
  Title,
  Button,
  Header,
  TermsCondition,
  CheckboxContainer,
  WarningContainer,
  Checkbox,
  TermsText,
  HeaderText,
  CloseImg,
  UsageGoalsContainer,
  UsageGoalsSubtitle,
  Chip,
  ChipGroup,
  OtherGoalInput,
  StepIndicator,
  ButtonRow,
  BackButton,
} from './stateYearDialogueStyle';
import { profileStateYear } from '../../service';
import StyledDropDown from '../StyledDropDown';
import { toast } from 'react-toastify';
import Toast from '../../components/Toast';
import { getLocalStorage, setLocalStorage } from '../../utils/function';
import { t, isUsCountry, isNzCountry } from '../../utils/lexicon';
import { useAvailableCountries } from '../../hooks/useSubjects';
import useGeoLocation from '../../hooks/useGeoLocation';

// AU school years run 7-12; US grades run 6-12 (ELA-ENG6 content exists).
// NZ spans Years 7-13: juniors (Yr7-10, NZ Curriculum / e-asTTle) and NCEA
// senior secondary (L1=Yr11, L2=Yr12, L3=Yr13).
const yearOptionsFor = (country) => {
  const years = isNzCountry(country)
    ? ['13', '12', '11', '10', '9', '8', '7']
    : isUsCountry(country)
    ? ['12', '11', '10', '9', '8', '7', '6']
    : ['12', '11', '10', '9', '8', '7'];
  return years.map((title) => ({ title }));
};

// NZ is a single national jurisdiction with one sentinel state "NZ".
const NZ_STATE_SENTINEL = 'NZ';

const STATE_PLACEHOLDER = { id: '__select_state__', title: 'Select state' };

// The country dropdown holds the catalog DISPLAY name ("United States"); the backend keys
// region scoping on the ISO-2 code ("US"). Map before saving so profile.country resolves
// correctly (the backend also canonicalizes, but sending the code keeps the contract clean).
const COUNTRY_CODES = {
  'United States': 'US',
  Australia: 'AU',
  'New Zealand': 'NZ',
  'United Kingdom': 'UK',
  Canada: 'CA',
  Singapore: 'SG',
};
const toCountryCode = (name) => {
  const raw = String(name || '').trim();
  if (COUNTRY_CODES[raw]) return COUNTRY_CODES[raw];
  // Already a 2-letter code → normalize case. An unknown display name passes through
  // unchanged (rather than becoming "SOUTH AFRICA"): the backend canonicalizes country on
  // read, and a country with no region content simply falls back to national tools.
  return /^[A-Za-z]{2}$/.test(raw) ? raw.toUpperCase() : raw;
};

// Onboarding survey: "What do you mainly want to use JeddAI for?" (teacher flow, multi-select).
const USAGE_GOAL_OPTIONS = [
  'Draft feedback assistance',
  'Independent student practice',
  'Pilot marking / double marking',
  'Identifying student skill gaps',
  'Peer or self-assessment',
];
const OTHER_GOAL_LABEL = 'Other (please specify)';

const StateYearDialogue = ({
  setStage,
  editStateYear,
  onClose,
  showCountry = true,
  showYear = true,
  showTerms = !editStateYear,
  showCloseButton = editStateYear,
  disableStateDefault = false,
  showUsageGoals = false,
  headerText,
  submitLabel,
  menuZIndex,
}) => {
  const { countriesAndStates, loading: loadingCountries } =
    useAvailableCountries();
  const {
    geoCountry,
    geoState,
    loading: geoLoading,
  } = useGeoLocation(countriesAndStates);

  const savedCountry = getLocalStorage('country');
  const savedStates = getLocalStorage('states');
  const savedState =
    Array.isArray(savedStates) && savedStates.length > 0
      ? savedStates[0]
      : getLocalStorage('state');

  const getInitialCountry = () => {
    // Trust a saved country directly — the catalog (countriesAndStates) loads async and is
    // {} on first render, so gating on countriesAndStates[savedCountry] would drop a returning
    // US user back to Australia and submit country: 'AU'. The dropdown resolves it once loaded.
    if (savedCountry) return savedCountry;
    return Object.keys(countriesAndStates)[0] || 'Australia';
  };

  const [country, setCountry] = useState(getInitialCountry);
  const [state, setState] = useState(() => {
    if (savedState != null) return savedState;
    if (disableStateDefault) return '';
    return '';
  });
  const defaultYear =
    getLocalStorage('year') === null || getLocalStorage('year') === undefined
      ? '7'
      : getLocalStorage('year');
  const [year, setYear] = useState(defaultYear);
  const [isCheckboxChecked, setIsCheckboxChecked] = useState(false);

  // Usage-goals survey (teacher onboarding). `usageGoals` holds selected predefined
  // labels; "Other" is tracked separately and resolved to its free text on submit.
  const [usageGoals, setUsageGoals] = useState([]);
  const [otherGoalSelected, setOtherGoalSelected] = useState(false);
  const [otherGoalText, setOtherGoalText] = useState('');

  // The non-school teacher onboarding is a 2-step wizard: screen 1 = location
  // (+ terms), screen 2 = the usage-goals survey. `screen` is only meaningful
  // when showUsageGoals is set; every other usage stays single-screen.
  const [screen, setScreen] = useState(1);

  const toggleUsageGoal = (label) => {
    setUsageGoals((prev) =>
      prev.includes(label)
        ? prev.filter((goal) => goal !== label)
        : [...prev, label]
    );
  };

  const trimmedOtherGoal = otherGoalText.trim();
  const usageGoalsValid =
    !showUsageGoals ||
    usageGoals.length > 0 ||
    (otherGoalSelected && trimmedOtherGoal.length > 0);

  const isNz = isNzCountry(country);

  const userHasSelectedCountry = useRef(false);
  const userHasSelectedState = useRef(false);

  // NZ has no curriculum subdivisions: auto-fill the single "NZ" sentinel and
  // hide the State dropdown. This also self-corrects the NZ->AU->NZ switch so a
  // stale AU state never strands on a NZ user, and keeps isSubmitDisabled
  // satisfiable (state is never empty) for NZ.
  useEffect(() => {
    if (isNz && state !== NZ_STATE_SENTINEL) {
      setState(NZ_STATE_SENTINEL);
    }
  }, [isNz, state]);

  // Apply geolocation defaults when resolved (only if user hasn't interacted and no saved values)
  useEffect(() => {
    if (geoLoading) return;

    if (
      !savedCountry &&
      !userHasSelectedCountry.current &&
      geoCountry &&
      countriesAndStates[geoCountry]
    ) {
      setCountry(geoCountry);
    }

    if (!savedState && !userHasSelectedState.current && geoState) {
      const effectiveCountry =
        !savedCountry &&
        !userHasSelectedCountry.current &&
        geoCountry &&
        countriesAndStates[geoCountry]
          ? geoCountry
          : country;
      const statesForCountry = countriesAndStates[effectiveCountry] || [];
      if (statesForCountry.includes(geoState)) {
        setState(geoState);
      }
    }
  }, [geoLoading, geoCountry, geoState, countriesAndStates]);

  // Re-derive country options when API data loads
  const countryOptions = Object.keys(countriesAndStates).map((c) => ({
    title: c,
  }));
  const statesForSelectedCountry = countriesAndStates[country] || [];
  const availableStateOptions = statesForSelectedCountry.map((s) => ({
    title: s,
  }));
  const stateOptions = disableStateDefault
    ? [STATE_PLACEHOLDER, ...availableStateOptions]
    : availableStateOptions;

  const handleStateSelect = (selectedState) => {
    userHasSelectedState.current = true;
    setState(
      selectedState?.id === STATE_PLACEHOLDER.id ? '' : selectedState.title
    );
  };

  const handleYearSelect = (selectedYear) => {
    setYear(selectedYear.title);
  };

  const yearOptions = yearOptionsFor(country);

  const handleCountrySelect = (selectedCountry) => {
    userHasSelectedCountry.current = true;
    setCountry(selectedCountry.title);
    const nextStates = countriesAndStates[selectedCountry.title] || [];
    if (!nextStates.includes(state)) {
      setState(disableStateDefault ? '' : nextStates[0] || '');
    }
    // A year only offered by the previous country (e.g. US grade 6) must not
    // survive the switch — it would be invisible in the dropdown yet submitted.
    if (year && !yearOptionsFor(selectedCountry.title).some((y) => y.title === year)) {
      setYear('');
    }
  };

  const saveStateYear = async () => {
    if (state && (!showYear || yearInOptions)) {
      const payload = { state, states: [state] };
      // Persist country so backend region scoping (picker + subject filter) attributes the
      // user correctly. Guarded on showCountry so a variant that hides the field (none today)
      // can't overwrite an existing country with a defaulted one.
      if (showCountry && country) {
        payload.country = toCountryCode(country);
      }
      if (showYear && yearInOptions) {
        payload.year = year;
      }
      if (showUsageGoals) {
        const resolvedGoals = [...usageGoals];
        if (otherGoalSelected && trimmedOtherGoal) {
          resolvedGoals.push(trimmedOtherGoal);
        }
        payload.usageGoals = resolvedGoals;
      }

      await profileStateYear(payload);
      setLocalStorage('country', country);
      setLocalStorage('state', state);
      setLocalStorage('states', [state]);
      if (showYear && yearInOptions) {
        setLocalStorage('year', year);
      }
      if (onClose) {
        await onClose();
      }
      if (editStateYear) {
        toast(
          <Toast
            message={
              showYear
                ? t('Year and location successfully updated', country)
                : 'Location successfully updated'
            }
          />
        );
        window.location.reload();
      } else if (!onClose) {
        window.location.reload();
      }
    }
  };

  const handleCheckboxChange = () => {
    setIsCheckboxChecked(!isCheckboxChecked);
  };

  const yearInOptions = !!year && yearOptions.some((y) => y.title === year);

  const isSubmitDisabled =
    !state ||
    (showYear && !yearInOptions) ||
    (showTerms && !isCheckboxChecked) ||
    !usageGoalsValid;

  // Per-step gating for the 2-step usage-goals wizard.
  const locationValid = !!state && (!showYear || yearInOptions);
  const termsValid = !showTerms || isCheckboxChecked;
  const step1Valid = locationValid && termsValid;

  const handleTermsConditionClick = () => {
    window.location.href = 'https://jeddle.com/terms-conditions/';
  };

  return (
    <DialogueBox>
      <Header>
        <HeaderText>
          {headerText ||
            (editStateYear
              ? 'Update your Location'
              : t("Let's Get Started - Customise Your Feedback", country))}
        </HeaderText>

        {showCloseButton && (
          <CloseImg
            src="img/vector-12@2x.png"
            onClick={() => {
              onClose?.();
            }}
          />
        )}
      </Header>
      {showUsageGoals && <StepIndicator>Step {screen} of 2</StepIndicator>}
      {(!showUsageGoals || screen === 1) && (
        <>
          <DropdownContainer>
            {showCountry && (
              <DropdownItem>
                <Title>Country</Title>
                <DropdownBox>
                  <StyledDropDown
                    menuItems={countryOptions}
                    selectedIndex={countryOptions.findIndex(
                      (option) => option.title === country
                    )}
                    fullWidth={true}
                    onItemSelected={handleCountrySelect}
                    menuZIndex={menuZIndex}
                  />
                </DropdownBox>
              </DropdownItem>
            )}
            {!isNz && (
              <DropdownItem>
                <Title>State</Title>
                <DropdownBox>
                  <StyledDropDown
                    menuItems={stateOptions}
                    selectedIndex={
                      state
                        ? stateOptions.findIndex(
                            (option) => option.title === state
                          )
                        : disableStateDefault
                        ? 0
                        : stateOptions.findIndex(
                            (option) => option.title === state
                          )
                    }
                    fullWidth={true}
                    onItemSelected={handleStateSelect}
                    menuZIndex={menuZIndex}
                  />
                </DropdownBox>
              </DropdownItem>
            )}
            {showYear && (
              <DropdownItem>
                <Title>{t('Year', country)}</Title>
                <DropdownBox>
                  <StyledDropDown
                    menuItems={yearOptions}
                    selectedIndex={yearOptions.findIndex(
                      (option) => option.title === year
                    )}
                    fullWidth={true}
                    onItemSelected={handleYearSelect}
                    menuZIndex={menuZIndex}
                  />
                </DropdownBox>
              </DropdownItem>
            )}
          </DropdownContainer>
          {showTerms && (
            <TermsCondition>
              <WarningContainer>
                <h3>Anti-Bullying & Harassment Policy</h3>
                <p>
                  Any instance of bullying, harassment or misconduct will result
                  in <span>immediate suspension</span> from the platform.
                </p>
              </WarningContainer>
              <CheckboxContainer>
                <Checkbox
                  type="checkbox"
                  checked={isCheckboxChecked}
                  onChange={handleCheckboxChange}
                />
                <TermsText>
                  I agree to the{' '}
                  <span onClick={handleTermsConditionClick}>
                    terms & conditions
                  </span>
                </TermsText>
              </CheckboxContainer>
            </TermsCondition>
          )}
        </>
      )}
      {showUsageGoals && screen === 2 && (
        <UsageGoalsContainer>
          <Title>What do you mainly want to use JeddAI for?</Title>
          <UsageGoalsSubtitle>Select all that apply</UsageGoalsSubtitle>
          <ChipGroup>
            {USAGE_GOAL_OPTIONS.map((option) => (
              <Chip
                key={option}
                type="button"
                selected={usageGoals.includes(option)}
                onClick={() => toggleUsageGoal(option)}
              >
                {option}
              </Chip>
            ))}
            <Chip
              type="button"
              selected={otherGoalSelected}
              onClick={() => setOtherGoalSelected((prev) => !prev)}
            >
              {OTHER_GOAL_LABEL}
            </Chip>
          </ChipGroup>
          {otherGoalSelected && (
            <OtherGoalInput
              type="text"
              value={otherGoalText}
              onChange={(e) => setOtherGoalText(e.target.value)}
              placeholder="Tell us more"
              autoFocus
            />
          )}
        </UsageGoalsContainer>
      )}
      {!showUsageGoals && (
        <Button onClick={saveStateYear} disabled={isSubmitDisabled}>
          {submitLabel || (editStateYear ? 'Update' : 'Submit')}
        </Button>
      )}
      {showUsageGoals && screen === 1 && (
        <Button
          type="button"
          onClick={() => setScreen(2)}
          disabled={!step1Valid}
        >
          Continue
        </Button>
      )}
      {showUsageGoals && screen === 2 && (
        <ButtonRow>
          <BackButton type="button" onClick={() => setScreen(1)}>
            Back
          </BackButton>
          <Button
            type="button"
            onClick={saveStateYear}
            disabled={!usageGoalsValid || !locationValid}
          >
            {submitLabel || 'Submit'}
          </Button>
        </ButtonRow>
      )}
    </DialogueBox>
  );
};

export default StateYearDialogue;
